import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, AlertTriangle } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { eventShareUrl } from "./CopyEventLinkButton";

const fileSafe = (s) => (s || "event").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "").slice(0, 60);

/** Printable QR code pointing at an event's booking or view link. */
export const EventQrDialog = ({ event, open, onOpenChange }) => {
  const [action, setAction] = useState("book");
  const hiResRef = useRef(null);
  const url = eventShareUrl(event.id, action);
  const dateLabel = event.event_date
    ? new Date(event.event_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";

  const pngData = () => hiResRef.current?.querySelector("canvas")?.toDataURL("image/png");

  const download = () => {
    const a = document.createElement("a");
    a.href = pngData();
    a.download = `QR_${fileSafe(event.title)}_${action}.png`;
    a.click();
  };

  const print = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    const esc = (t) => String(t || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    w.document.write(`<!doctype html><html><head><title>${esc(event.title)}</title>
      <style>body{font-family:Georgia,serif;text-align:center;padding:40px;color:#111}
      h1{font-size:26px;margin:0 0 8px}p{margin:4px 0;color:#444}img{width:420px;height:420px;margin:28px auto}
      .cta{font-size:20px;font-weight:bold;color:#111}.url{font-family:monospace;font-size:11px;color:#666;word-break:break-all}</style>
      </head><body><h1>${esc(event.title)}</h1><p>${esc(dateLabel)}</p>
      <img src="${pngData()}" alt="QR code"/>
      <p class="cta">${action === "book" ? "Scan to book your ticket" : "Scan for event details"}</p>
      <p class="url">${esc(url)}</p></body></html>`);
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="event-qr-dialog">
        <DialogHeader>
          <DialogTitle>QR code for posters & flyers</DialogTitle>
          <DialogDescription className="line-clamp-2">{event.title}</DialogDescription>
        </DialogHeader>

        <div className="flex rounded-md border border-slate-200 p-1 bg-slate-50 text-sm" role="tablist">
          {[["book", "Booking form"], ["view", "Event details"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setAction(key)}
              className={`flex-1 rounded px-3 py-1.5 transition-colors ${action === key ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`event-qr-target-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-col items-center gap-2 py-2">
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="event-qr-code">
            <QRCodeCanvas value={url} size={220} level="M" marginSize={2} />
          </div>
          <p className="text-[11px] font-mono text-slate-500 break-all text-center px-2" data-testid="event-qr-url">{url}</p>
        </div>

        {event.status === "draft" && (
          <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="event-qr-draft-warning">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            This event is still a draft: members can only open it once it is published.
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={print} data-testid="event-qr-print-btn">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button onClick={download} data-testid="event-qr-download-btn">
            <Download className="h-4 w-4 mr-1" /> Download PNG
          </Button>
        </div>

        {/* High-resolution copy used for download / print */}
        <div ref={hiResRef} className="hidden" aria-hidden="true">
          <QRCodeCanvas value={url} size={1024} level="M" marginSize={4} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
